<template>
	<DataDialog
		ref="userDialog"
		v-model="dialogUser"
		:title="dialogMode === 'add' ? $t('text.addUser') : $t('text.editUser')"
		:validator="validateUser"
		:reactOn="{ email: dialogUser.email, emailStatus: emailStatus }"
		:onConfirm="handleDialogUpdate"
		width="720px"
	>
		<template #content="{ model }">
			<Alert v-model="errorAlert" type="error" />
			<div class="form-fields">
				<div class="two-col">
					<div>
						<v-label>{{ $t('text.firstName') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
						<v-text-field 
							v-model="model.firstName" 
							variant="outlined" 
							density="compact" 
							hide-details
							data-cy="firstName"
						/>
					</div>
					<div>
						<v-label>{{ $t('text.lastName') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
						<v-text-field 
							v-model="model.lastName" 
							variant="outlined" 
							density="compact" 
							hide-details
							data-cy="lastName"
						/>
					</div>
				</div>
				<div>
					<v-label>{{ $t('text.loginEmailAddress') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
					<v-text-field 
						v-model="model.email" 
						variant="outlined" 
						density="compact"
						:loading="checkingEmail"
						:messages="emailStatusMessage"
						:color="emailStatusColor"
						:hide-details="!emailStatusMessage"
						:disabled="isAccepted"
						@input="debouncedCheckEmail"
						data-cy="email"
					/>
				</div>
				<div>
					<v-label>{{ $t('text.preferredLanguage') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
					<v-select
						v-model="model.language"
						:items="languages"
						item-title="label"
						item-value="value"
						variant="outlined"
						density="compact"
						hide-details
						data-cy="language"
					>
						<template v-slot:item="{ props, item }">
							<v-list-item v-bind="props" class="preferred-language-list-item">
								<div style="display: flex; align-items: center;">
									<LanguageFlag v-model="item.value" />
									<span style="margin-left: 10px;">{{ item.title }}</span>
								</div>
							</v-list-item>
						</template>
						<template v-slot:selection="{ item }">
							<div style="display: flex; align-items: center;">
								<LanguageFlag v-model="item.value" />
								<span style="margin-left: 10px;">{{ item.title }}</span>
							</div>
						</template>
					</v-select>
				</div>
				<div>
					<v-label>{{ $t('text.userRole') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
					<v-select
						v-model="model.userRole"
						:items="roleOptions"
						item-title="label"
						item-value="value"
						variant="outlined"
						density="compact"
						hide-details
						:disabled="isAccepted"
						data-cy="userRole"
					/>
				</div>
				<div v-if="!isAccepted">
					<p class="helpText">{{ $t('text.userInvitationHelp') }}</p>
				</div>
			</div>
		</template>
	</DataDialog>
</template>

<script>
import DataDialog from '@/components/common/DataDialog.vue'
import LanguageFlag from '@/components/common/LanguageFlag.vue'
import InvitationExecutive from '../../../../../api2/src/modules/trade-hub/InvitationExecutive'
import { InvitationRole } from '../../../../../shared/types/account-types'
import Alert from '@/components/common/Alert.vue'

export default {
	name: 'AddUserDialog',
	components: { DataDialog, LanguageFlag, Alert },
	props: {
		modelValue: {
			type: Boolean,
			default: false
		},
		mode: {
			type: String,
			default: 'add', // 'add' | 'edit'
			validator: value => ['add', 'edit'].includes(value)
		},
		user: {
			type: Object,
			default: () => ({ firstName: '', lastName: '', email: '', language: 'en', userRole: 'User' })
		},
		serviceProviderId: {
			type: String,
			required: true
		}
	},
	emits: ['update:modelValue', 'user-saved', 'user-updated'],
	data() {
		return {
			dialogUser: { firstName: '', lastName: '', email: '', language: 'en', userRole: 'User' },
			errorAlert: null,
			languages: [
				{ label: 'English', value: 'en' },
				{ label: 'Deutsch', value: 'de' },
				{ label: 'Français', value: 'fr' },
				{ label: 'Italiano', value: 'it' },
				{ label: 'Nederlands', value: 'nl' }
			],
			roleOptions: [
				{ label: 'Admin', value: InvitationRole.ADMIN },
				{ label: 'User', value: InvitationRole.USER },
			],
			// Email validation
			checkingEmail: false,
			emailStatus: null,
			emailCheckTimeout: null,
		}
	},
	computed: {
		dialogMode() {
			return this.mode
		},
		isAccepted() {
			return this.dialogUser?.status === 'ACCEPTED'
		},
		emailStatusMessage() {
			if (!this.emailStatus) return ''
			return this.emailStatus.message || ''
		},
		emailStatusColor() {
			if (!this.emailStatus) return ''
			if (this.emailStatus.available === false) {
				if (this.emailStatus.status === 'ACCEPTED') return 'error'
				if (this.emailStatus.status === 'SENT') return 'warning'
				return 'error'
			}
			if (this.emailStatus.available === true) return 'success'
			return ''
		}
	},
	watch: {
		user: {
			immediate: true,
			deep: true,
			handler(newUser) {
				this.dialogUser = { ...newUser }
			}
		},
	},
	methods: {
		open() {
			this.errorAlert = null
			this.emailStatus = null // Reset email status
			this.$nextTick(() => this.$refs.userDialog.open())
		},
		validateUser(model) {
			if (!model) return this.$t('text.required')
			if (!model.firstName?.trim()) return this.$t('text.firstNameRequired')
			if (!model.lastName?.trim()) return this.$t('text.lastNameRequired')
			if (!model.email?.trim()) return this.$t('text.emailRequired')
			
			// Check email status for blocking conditions
			if (this.emailStatus && !this.emailStatus.available) {
				if (this.emailStatus.status === 'ACCEPTED') {
					return 'User has already accepted an invitation'
				}
				if (this.emailStatus.status === 'SENT') {
					return 'Invitation already sent to this email address'
				}
				if (this.emailStatus.message && this.emailStatus.message.includes('valid email')) {
					return this.$t('text.validEmailRequired')
				}
			}
			
			if (!model.language) return this.$t('text.languageRequired')
			if (!model.userRole) return this.$t('text.userRoleRequired')
			return null
		},
		async handleDialogUpdate(model) {
			if (!model) return

			this.errorAlert = null
			try {
				const executive = new InvitationExecutive(this)
				
				if (this.dialogMode === 'add') {
					const inviterUserId = !this.$store.state.isImpersonation ? 
						this.$store.state.loggedInUser.sys.id : 
						this.$store.state.selectedServiceProvider.fields.userAccount.de.sys.id
					
					console.log('Creating invitation with serviceProviderId:', this.serviceProviderId)
					console.log('Request data:', {
						clientId: this.$store.state.selectedClient.sys.id,
						inviterServiceProviderId: this.serviceProviderId,
						inviterUserId,
						firstName: model.firstName,
						lastName: model.lastName,
						email: model.email,
						preferredLocale: this.mapLanguageToLocale(model.language),
						role: model.userRole
					})
						
					await executive.createAndSendInvitation(this.serviceProviderId, {
						clientId: this.$store.state.selectedClient.sys.id,
						inviterServiceProviderId: this.serviceProviderId,
						inviterUserId,
						firstName: model.firstName,
						lastName: model.lastName,
						email: model.email,
						preferredLocale: this.mapLanguageToLocale(model.language),
						role: model.userRole
					})
					this.$emit('user-saved', model)
				} else if (this.dialogMode === 'edit') {
					// Handle edit mode - update existing invitation
					if (model.status === 'ACCEPTED') {
						const response = await executive.updateUser(model.invitationId, {
							invitationId: model.invitationId,
							firstName: model.firstName,
							lastName: model.lastName,
							preferredLocale: this.mapLanguageToLocale(model.language)
						})
						if (!response.success) {
							throw new Error(response.errors?.join(', ') || 'Failed to update user')
						}
					} else {
						await executive.updateInvitation(model.invitationId, {
							invitationId: model.invitationId,
							firstName: model.firstName,
							lastName: model.lastName,
							email: model.email,
							preferredLocale: this.mapLanguageToLocale(model.language),
							role: model.userRole
						})
					}
					this.$emit('user-updated', model)
				}
			} catch (error) {
				console.error('Failed to save user:', error)
				const message = error?.message || String(error || '') || 'Failed to save user'
				this.errorAlert = message
				throw error
			}
		},
		// Email validation methods
		debouncedCheckEmail(event) {
			console.log('debouncedCheckEmail called with:', event, typeof event)
			
			// Clear previous timeout
			if (this.emailCheckTimeout) {
				clearTimeout(this.emailCheckTimeout)
			}

			// Get email value - handle both event object and direct value
			let email = event
			if (event && event.target) {
				email = event.target.value
			}

			// Convert to string and check if empty
			const emailStr = String(email || '').trim()
			console.log('Email string:', emailStr)
			
			if (!emailStr) {
				this.emailStatus = null
				return
			}

			// Set timeout for debounced check
			this.emailCheckTimeout = setTimeout(() => {
				console.log('Calling checkEmailAvailability with:', emailStr)
				this.checkEmailAvailability(emailStr)
			}, 500) // 500ms delay
		},
		async checkEmailAvailability(email) {
			// Basic email validation
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
			if (!emailRegex.test(email)) {
				this.emailStatus = {
					available: false,
					message: this.$t('text.validEmailRequired')
				}
				return
			}

			this.checkingEmail = true
			try {
				const executive = new InvitationExecutive(this)
				const result = await executive.checkEmailAvailability(email)
				this.emailStatus = result
			} catch (error) {
				console.error('Failed to check email availability:', error)
				this.emailStatus = {
					available: true,
					message: 'Unable to check email status'
				}
			} finally {
				this.checkingEmail = false
			}
		},
		mapLanguageToLocale(language) {
			// Since we're now using locale codes directly, just return the language
			return language || 'en'
		}
	},
	mounted() {
		console.log('serviceProviderId', this.serviceProviderId)
	},
	beforeUnmount() {
		// Clean up timeout
		if (this.emailCheckTimeout) {
			clearTimeout(this.emailCheckTimeout)
		}
	}
}
</script>

<style lang="scss" scoped>
.form-fields {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.two-col {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
}

.error-text {
	color: red;
	font-size: 0.875em;
}

.helpText {
	margin-top: 8px;
	color: #666;
	font-size: 0.875em;
	line-height: 1.4;
}

.status-message {
	margin-top: 4px;
	font-size: 0.875em;
	line-height: 1.4;
	
	&.error {
		color: #d32f2f;
	}
	
	&.warning {
		color: #ed6c02;
	}
	
	&.success {
		color: #2e7d32;
	}
}
</style>

<style lang="scss">
.v-list:has(.preferred-language-list-item) {
	padding: 0px;
	.preferred-language-list-item {
		padding-inline: 12px;
		min-height: 60px;
		padding-top: 7px;
		padding-bottom: 7px;
		border-bottom: 1px solid #e5e5e5;

		&:hover {
			cursor: pointer !important;
		}
		.v-list-item__overlay,
		.v-list-item-title,
		.v-list-item__spacer {
			display: none;
		}
		.v-list-item-action {
			margin-inline-end: 0px;
		}
		&.v-list-item--active {
			color: inherit !important;
		}
	}
}
</style>
